

import React, { useState, useEffect, useRef } from 'react';
import socket from '../../../socket/socket';
import axios from 'axios';
import { Spinner, Modal, Button } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import { 
  BsHeadset, BsSend, BsMic, BsPlusCircle, BsTelephone, 
  BsMicMute, BsVolumeUp, BsXCircle, BsList, BsCheckAll, 
  BsChevronLeft, BsTrash, BsPlayFill, BsPauseFill, BsFillTelephoneFill, BsFileEarmarkText,
  BsShieldCheck, BsPersonBadge, BsPaperclip, BsPerson, BsTelephoneOutbound, BsArrowRightShort
} from 'react-icons/bs';
import './AdminSupportChat.css';
import '../messaging/Chat.css'; // Reuse existing chat bubble styles if helpful

const beckendLocalApiUrl = process.env.REACT_APP_BACKEND_LOCAL_API 
const beckendLiveApiUrl = process.env.REACT_APP_BACKEND_LIVE_API 
const baseUrl = "https://backend.mplbets.com";



const AdminSupportChat = () => {
  const token = localStorage.getItem('token');

  // State
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('waiting'); // 'waiting', 'active', 'closed'
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isOpponentTyping, setIsOpponentTyping] = useState(false);
  const [typing, setTyping] = useState(false);

  // Filters
  const [priorityFilter, setPriorityFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Lists & Metadata
  const [agentsList, setAgentsList] = useState([]);
  const [internalNotes, setInternalNotes] = useState('');
  const [stats, setStats] = useState({ total: 0, waiting: 0, active: 0, closed: 0, onlineAgents: 0 });
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // File Upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Voice recording
  const [recording, setRecording] = useState(false);
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [voiceDuration, setVoiceDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordIntervalRef = useRef(null);

  // WebRTC calling
  const [callActive, setCallActive] = useState(false);
  const [callState, setCallState] = useState(''); // 'ringing', 'calling', 'connected', 'disconnected'
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [incomingCallData, setIncomingCallData] = useState(null);
  const [currentCallId, setCurrentCallId] = useState(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const callTimerRef = useRef(null);

  const messagesEndRef = useRef(null);

  // Get current Admin/Agent profile
  useEffect(() => {
    if (!token) {
      toast.error('Admin token missing.');
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };

    axios.get(`${baseUrl}/admin/me_data`, { headers })
      .then(res => {
        setCurrentUser(res.data);
      })
      .catch(() => {
        toast.error('Failed to load profile.');
      });

    fetchStats();
    fetchAgents();

    // Listen to waiting queue updates globally
    socket.on('support:status:waiting_queue_update', () => {
      fetchStats();
      fetchTicketsList();
    });

    socket.on('support:status:agent_panel_update', () => {
      fetchStats();
      fetchTicketsList();
    });

    return () => {
      socket.off('support:status:waiting_queue_update');
      socket.off('support:status:agent_panel_update');
    };
  }, []);

  // Fetch lists when tab or filters change
  useEffect(() => {
    fetchTicketsList();
  }, [activeTab, priorityFilter, searchQuery, page]);

  const fetchStats = () => {
    axios.get(`${baseUrl}/chat/support/stats`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        setStats(res.data);
      });
  };

  const fetchAgents = () => {
    axios.get(`${baseUrl}/chat/support/agents`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        setAgentsList(res.data);
      });
  };

  const fetchTicketsList = () => {
    if (!token) return;
    setLoadingTickets(true);
    let url = `${baseUrl}/chat/support/list?page=${page}&limit=20`;
    if (activeTab === 'waiting') url += '&status=waiting';
    else if (activeTab === 'active') url += `&status=active`;
    else if (activeTab === 'closed') url += '&status=closed';

    if (priorityFilter) url += `&priority=${priorityFilter}`;
    if (searchQuery) url += `&search=${searchQuery}`;

    axios.get(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setTickets(res.data.results || []);
        setTotalPages(res.data.totalPages || 1);
        setLoadingTickets(false);
      })
      .catch(() => {
        setLoadingTickets(false);
      });
  };

  // Select Chat and Join
  const selectTicket = (ticket) => {
    setActiveTicket(ticket);
    setMessages([]);
    setLoadingMessages(true);
    setInternalNotes(ticket.internalNotes || '');

    axios.get(`${baseUrl}/chat/support/${ticket._id}/messages?limit=100`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        setMessages(res.data.results || []);
        setLoadingMessages(false);
        setTimeout(scrollToBottom, 100);
      })
      .catch(() => {
        setLoadingMessages(false);
      });

    socket.emit('support:join', { chatId: ticket._id, token });
  };

  // Chat real-time hooks
  useEffect(() => {
    if (!activeTicket) return;

    socket.on('support:message', (msg) => {
      if (msg.chatId === activeTicket._id) {
        setMessages(prev => {
          if (prev.find(m => m._id === msg._id)) return prev;
          return [...prev, msg];
        });
        setTimeout(scrollToBottom, 100);
        socket.emit('support:seen', { chatId: activeTicket._id });
      }
    });

    socket.on('support:seen', (data) => {
      if (data.chatId === activeTicket._id) {
        setMessages(prev => prev.map(m => m.senderType === 'User' ? m : { ...m, seen: true, seenAt: new Date() }));
      }
    });

    socket.on('support:typing', (data) => {
      if (data.chatId === activeTicket._id && data.userType === 'User') {
        setIsOpponentTyping(data.isTyping);
      }
    });

    socket.on('support:status', (data) => {
      if (data.chat._id === activeTicket._id) {
        setActiveTicket(data.chat);
        setInternalNotes(data.chat.internalNotes || '');
        if (data.message) {
          setMessages(prev => [...prev, data.message]);
          setTimeout(scrollToBottom, 100);
        }
      }
    });

    // WebRTC calling hooks
    socket.on('support:call:ringing', (data) => {
      setIncomingCallData(data);
      setCallState('ringing');
      setCallActive(true);
    });

    socket.on('support:call:action', (data) => {
      if (data.status === 'connected') {
        setCallState('connected');
        startCallTimer();
      } else if (['rejected', 'completed', 'missed'].includes(data.status)) {
        endCallCleanUp();
      }
    });

    socket.on('support:call:signal', async (data) => {
      if (!peerConnectionRef.current) return;
      const { signalData } = data;
      if (signalData.type === 'offer') {
        await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(signalData.offer));
        const answer = await peerConnectionRef.current.createAnswer();
        await peerConnectionRef.current.setLocalDescription(answer);
        socket.emit('support:call:signal', {
          chatId: activeTicket._id,
          signalData: { type: 'answer', answer }
        });
      } else if (signalData.type === 'answer') {
        await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(signalData.answer));
      } else if (signalData.type === 'candidate') {
        try {
          await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(signalData.candidate));
        } catch (e) {
          console.error(e);
        }
      }
    });

    return () => {
      socket.off('support:message');
      socket.off('support:seen');
      socket.off('support:typing');
      socket.off('support:status');
      socket.off('support:call:ringing');
      socket.off('support:call:action');
      socket.off('support:call:signal');
    };
  }, [activeTicket]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Claims / Accept waiting chat
  const handleClaimTicket = () => {
    axios.post(`${baseUrl}/chat/support/${activeTicket._id}/assign`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        toast.success('Ticket claimed successfully.');
        setActiveTicket(res.data);
        fetchStats();
        fetchTicketsList();
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Failed to claim ticket.');
      });
  };

  // Send message
  const handleSendMessage = () => {
    if (!inputText.trim() && !selectedFile) return;
    socket.emit('support:message', {
      chatId: activeTicket._id,
      message: inputText.trim(),
      messageType: selectedFile ? selectedFile.type : 'text',
      attachment: selectedFile ? selectedFile.url : null
    });
    setInputText('');
    setSelectedFile(null);
  };

  const handleTextInputChange = (e) => {
    setInputText(e.target.value);
    if (!typing) {
      setTyping(true);
      socket.emit('support:typing', { chatId: activeTicket._id, isTyping: true });
    }
    clearTimeout(window.typingTimeout);
    window.typingTimeout = setTimeout(() => {
      setTyping(false);
      socket.emit('support:typing', { chatId: activeTicket._id, isTyping: false });
    }, 2000);
  };

  // Close / Reopen Chat
  const handleCloseChat = () => {
    axios.post(`${baseUrl}/chat/support/${activeTicket._id}/close`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        toast.success('Support chat ticket closed.');
        setActiveTicket(res.data);
        fetchStats();
        fetchTicketsList();
      });
  };

  const handleReopenChat = () => {
    axios.post(`${baseUrl}/chat/support/${activeTicket._id}/reopen`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        toast.success('Support chat ticket reopened.');
        setActiveTicket(res.data);
        fetchStats();
        fetchTicketsList();
      });
  };

  // Save Notes
  const handleSaveNotes = () => {
    axios.post(`${baseUrl}/chat/support/${activeTicket._id}/notes`, { internalNotes }, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(() => {
        toast.success('Internal notes updated.');
      });
  };

  // Transfer Chat
  const handleTransferChat = (agentId) => {
    axios.post(`${baseUrl}/chat/support/${activeTicket._id}/transfer`, { assignedAgentId: agentId }, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        toast.success('Chat transferred.');
        setActiveTicket(res.data);
        fetchStats();
        fetchTicketsList();
      })
      .catch(() => {
        toast.error('Failed to transfer chat.');
      });
  };

  // File Upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploadingFile(true);
    axios.post(`${baseUrl}/chat/support/upload`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    })
      .then(res => {
        let fileType = 'file';
        if (file.type.startsWith('image/')) fileType = 'image';

        setSelectedFile({
          url: res.data.url,
          type: fileType,
          name: file.name
        });
        setUploadingFile(false);
      })
      .catch(() => {
        toast.error('Upload failed.');
        setUploadingFile(false);
      });
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;

      audioChunksRef.current = [];
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setVoiceBlob(audioBlob);
      };

      mediaRecorder.start();
      setRecording(true);
      setVoiceDuration(0);

      recordIntervalRef.current = setInterval(() => {
        setVoiceDuration(prev => prev + 1);
      }, 1000);
    } catch (e) {
      toast.error('Microphone unavailable.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      clearInterval(recordIntervalRef.current);
      setRecording(false);
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(t => t.stop());
      }
    }
  };

  const sendVoiceMessage = () => {
    if (!voiceBlob) return;
    const file = new File([voiceBlob], 'voice-message.webm', { type: 'audio/webm' });
    const formData = new FormData();
    formData.append('file', file);

    setUploadingFile(true);
    axios.post(`${baseUrl}/chat/support/upload`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    })
      .then(res => {
        socket.emit('support:message', {
          chatId: activeTicket._id,
          message: '',
          messageType: 'voice',
          attachment: res.data.url
        });
        setVoiceBlob(null);
        setVoiceDuration(0);
        setUploadingFile(false);
      })
      .catch(() => {
        toast.error('Failed to send voice message.');
        setUploadingFile(false);
      });
  };

  // WebRTC audio calls
  const initializePeerConnection = () => {
    const configuration = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] };
    const pc = new RTCPeerConnection(configuration);

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        socket.emit('support:call:signal', {
          chatId: activeTicket._id,
          signalData: { type: 'candidate', candidate: event.candidate }
        });
      }
    };

    pc.ontrack = (event) => {
      if (remoteAudioRef.current) remoteAudioRef.current.srcObject = event.streams[0];
    };

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
      });
    }

    peerConnectionRef.current = pc;
  };

  const startVoiceCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;

      setCallActive(true);
      setCallState('calling');

      initializePeerConnection();

      socket.emit('support:call:initiate', {
        chatId: activeTicket._id,
        callerId: currentUser._id,
        receiverId: activeTicket.userId._id
      });

      const offer = await peerConnectionRef.current.createOffer();
      await peerConnectionRef.current.setLocalDescription(offer);
      socket.emit('support:call:signal', {
        chatId: activeTicket._id,
        signalData: { type: 'offer', offer }
      });
    } catch (e) {
      toast.error('Microphone access failed.');
    }
  };

  const acceptCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;

      setCallState('connected');
      initializePeerConnection();

      socket.emit('support:call:action', {
        chatId: incomingCallData.chatId,
        callId: incomingCallData.callId,
        action: 'accept'
      });

      startCallTimer();
    } catch (e) {
      toast.error('Audio stream setup failed.');
      rejectCall();
    }
  };

  const rejectCall = () => {
    if (incomingCallData) {
      socket.emit('support:call:action', {
        chatId: incomingCallData.chatId,
        callId: incomingCallData.callId,
        action: 'reject'
      });
    }
    endCallCleanUp();
  };

  const hangupCall = () => {
    socket.emit('support:call:action', {
      chatId: activeTicket._id,
      callId: currentCallId || (incomingCallData && incomingCallData.callId),
      action: 'hangup'
    });
    endCallCleanUp();
  };

  const endCallCleanUp = () => {
    setCallActive(false);
    setCallState('');
    setIncomingCallData(null);
    setCurrentCallId(null);
    setIsMuted(false);
    clearInterval(callTimerRef.current);
    setCallDuration(0);

    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setIsMuted(!track.enabled);
      }
    }
  };

  const startCallTimer = () => {
    setCallDuration(0);
    callTimerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
  };

  const formatDuration = (sec) => {
    const min = Math.floor(sec / 60);
    const rSec = sec % 60;
    return `${min.toString().padStart(2, '0')}:${rSec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="admin-chat-layout">
      <ToastContainer theme="light" />
      <audio ref={remoteAudioRef} autoPlay />

      {/* COLUMN 1: SIDEBAR LISTS */}
      <div className="admin-chat-sidebar">
        {/* Top Header & Quick Stats Banner */}
        <div className="admin-sidebar-top">
          <div className="sidebar-brand-title">
            <BsShieldCheck className="brand-icon" />
            <span>Support Command</span>
          </div>
          <div className="admin-stats-pills">
            <div className="stat-pill waiting">
              <span className="stat-value">{stats.waiting}</span>
              <span className="stat-label">Waiting</span>
            </div>
            <div className="stat-pill active">
              <span className="stat-value">{stats.active}</span>
              <span className="stat-label">Active</span>
            </div>
            <div className="stat-pill closed">
              <span className="stat-value">{stats.closed}</span>
              <span className="stat-label">Closed</span>
            </div>
            <div className="stat-pill online">
              <span className="stat-value">{stats.onlineAgents}</span>
              <span className="stat-label">Online</span>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="sidebar-tabs">
          <button 
            className={`sidebar-tab-btn ${activeTab === 'waiting' ? 'active' : ''}`} 
            onClick={() => { setActiveTab('waiting'); setActiveTicket(null); }}
          >
            Queue ({stats.waiting})
          </button>
          <button 
            className={`sidebar-tab-btn ${activeTab === 'active' ? 'active' : ''}`} 
            onClick={() => { setActiveTab('active'); setActiveTicket(null); }}
          >
            My Active
          </button>
          <button 
            className={`sidebar-tab-btn ${activeTab === 'closed' ? 'active' : ''}`} 
            onClick={() => { setActiveTab('closed'); setActiveTicket(null); }}
          >
            Closed
          </button>
        </div>

        {/* Filters */}
        <div className="sidebar-filters">
          <input 
            type="text" 
            className="form-control form-control-sm search-input" 
            placeholder="Search Ticket / User ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select className="form-control form-control-sm priority-select" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
            <option value="">All Priority</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        {/* Ticket List */}
        <div className="ticket-list">
          {loadingTickets ? (
            <div className="text-center py-5">
              <Spinner size="sm" animation="border" variant="primary" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="empty-state-list">No tickets found in this tab.</div>
          ) : (
            tickets.map(t => (
              <div 
                key={t._id} 
                className={`ticket-item ${activeTicket && activeTicket._id === t._id ? 'active' : ''}`}
                onClick={() => selectTicket(t)}
              >
                <div className="ticket-item-header">
                  <span className="ticket-id">#{t.ticketId || t._id.substring(18)}</span>
                  <span className={`ticket-status-badge status-${t.status}`}>{t.status}</span>
                </div>
                <p className="ticket-subject">{t.subject}</p>
                <div className="ticket-meta">
                  <span className="ticket-user-name">
                    <BsPerson size={12} /> {t.userId?.Name || 'Customer'}
                  </span>
                  <span className={`priority-tag priority-${t.priority}`}>{t.priority}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* COLUMN 2: MAIN ACTIVE CHAT WINDOW */}
      <div className="admin-chat-window">
        {activeTicket ? (
          <>
            {/* Window Header */}
            <div className="chat-window-header">
              <div className="user-info-box">
                <div className="user-avatar-gradient">
                  {activeTicket.userId?.Name ? activeTicket.userId.Name[0].toUpperCase() : 'C'}
                </div>
                <div className="user-meta">
                  <h4>{activeTicket.userId?.Name || 'Customer'}</h4>
                  <span>Ticket ID: <strong>#{activeTicket.ticketId || activeTicket._id.substring(18)}</strong> • Priority: <strong className={`text-priority-${activeTicket.priority}`}>{activeTicket.priority}</strong></span>
                </div>
              </div>

              <div className="header-actions">
                {activeTicket.status === 'active' && (
                  <button className="header-action-btn call-btn" onClick={startVoiceCall} title="Call Customer">
                    <BsTelephoneOutbound size={16} />
                  </button>
                )}
                {activeTicket.status !== 'closed' ? (
                  <button className="btn btn-sm btn-danger-soft" onClick={handleCloseChat}>
                    <BsXCircle /> Close Ticket
                  </button>
                ) : (
                  <button className="btn btn-sm btn-success-soft" onClick={handleReopenChat}>
                    Reopen Ticket
                  </button>
                )}
              </div>
            </div>

            {/* Messages Stream */}
            <div className="messages-container">
              {loadingMessages ? (
                <div className="text-center my-auto"><Spinner animation="border" variant="primary" /></div>
              ) : (
                messages.map((m, idx) => {
                  const isAgent = m.senderType === 'Agent' || m.senderType === 'Admin';
                  return (
                    <div key={m._id || idx} className={`message-wrapper ${m.senderType.toLowerCase()}`}>
                      <div className="message-bubble">
                        <span className="sender-badge">{m.senderType}</span>

                        {m.senderType === 'System' ? (
                          <span className="system-msg-text">{m.message}</span>
                        ) : (
                          <>
                            {m.messageType === 'image' && m.attachment && (
                              <div className="message-media">
                                <img src={`${baseUrl}${m.attachment}`} alt="Attached file" onClick={() => window.open(`${baseUrl}${m.attachment}`, '_blank')} />
                              </div>
                            )}

                            {m.messageType === 'file' && m.attachment && (
                              <div className="message-media py-1">
                                <a href={`${baseUrl}${m.attachment}`} target="_blank" rel="noreferrer" className="attachment-link">
                                  <BsPaperclip /> View Attached Document
                                </a>
                              </div>
                            )}

                            {m.messageType === 'voice' && m.attachment && (
                              <div className="audio-player-wrapper">
                                <audio src={`${baseUrl}${m.attachment}`} controls />
                              </div>
                            )}

                            {m.message && <p className="message-text">{m.message}</p>}

                            <div className="message-info">
                              <span>{new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              {isAgent && <BsCheckAll className={`seen-icon ${m.seen ? 'read' : ''}`} />}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              )}

              {isOpponentTyping && (
                <div className="typing-indicator-bubble">
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Composer Bar */}
            {activeTicket.status === 'waiting' ? (
              <div className="p-3 bg-white text-center border-top shadow-sm">
                <button className="btn btn-claim-ticket" onClick={handleClaimTicket}>
                  <BsCheckAll size={20} /> Claim & Assign Ticket to Me
                </button>
              </div>
            ) : activeTicket.status === 'closed' ? (
              <div className="p-3 bg-light text-center text-muted border-top font-weight-bold" style={{fontSize: '0.88rem'}}>
                This ticket is currently closed. Reopen to respond.
              </div>
            ) : (
              <div className="chat-composer">
                {selectedFile && (
                  <div className="upload-preview-card">
                    <span>📎 File Selected: <strong>{selectedFile.name}</strong></span>
                    <button onClick={() => setSelectedFile(null)}>Remove</button>
                  </div>
                )}

                <div className="composer-row">
                  <input type="file" id="admin-file" style={{display: 'none'}} onChange={handleFileUpload} />
                  <button className="composer-icon-btn" onClick={() => document.getElementById('admin-file').click()} disabled={uploadingFile} title="Attach Document">
                    {uploadingFile ? <Spinner size="sm" animation="border" variant="primary" /> : <BsPaperclip size={20} />}
                  </button>

                  {recording ? (
                    <div className="voice-recorder-banner">
                      <div className="voice-recording-status">
                        <div className="recording-dot-blink"></div>
                        <span>Recording Voice ({formatDuration(voiceDuration)})</span>
                      </div>
                      <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-danger py-1 px-3" onClick={stopRecording}>Stop</button>
                        <button className="btn btn-sm btn-light border py-1 px-2" onClick={() => { setRecording(false); setVoiceBlob(null); }}>Cancel</button>
                      </div>
                    </div>
                  ) : voiceBlob ? (
                    <div className="voice-recorder-banner">
                      <span>Audio recording ready</span>
                      <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-success text-white py-1 px-3" onClick={sendVoiceMessage}>Send Voice</button>
                        <button className="btn btn-sm btn-outline-danger py-1 px-2" onClick={() => setVoiceBlob(null)}><BsTrash /></button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <input 
                        type="text" 
                        className="composer-input"
                        placeholder="Write support response..."
                        value={inputText}
                        onChange={handleTextInputChange}
                        onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                      />
                      <button className="composer-icon-btn" onClick={startRecording} title="Record Voice">
                        <BsMic size={19} />
                      </button>
                    </>
                  )}

                  <button className="composer-send-btn" onClick={handleSendMessage} title="Send Response">
                    <BsSend size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="chat-empty-state my-auto">
            <div className="empty-icon-circle">
              <BsHeadset />
            </div>
            <h3>Agent Command Dashboard</h3>
            <p>Select an incoming ticket from the left panel to begin support interactions.</p>
          </div>
        )}
      </div>

      {/* COLUMN 3: USER META INFO & ACTIONS */}
      {activeTicket && (
        <div className="admin-chat-meta-panel">
          <div className="meta-card">
            <div className="meta-card-header">
              <BsPersonBadge />
              <h5>User Information</h5>
            </div>
            <div className="meta-info-list">
              <div className="meta-info-item">
                <span className="label">Full Name:</span>
                <span className="value">{activeTicket.userId?.Name || 'N/A'}</span>
              </div>
              <div className="meta-info-item">
                <span className="label">Phone:</span>
                <span className="value">{activeTicket.userId?.Phone || 'N/A'}</span>
              </div>
              <div className="meta-info-item">
                <span className="label">Email:</span>
                <span className="value">{activeTicket.userId?.Email || 'N/A'}</span>
              </div>
              <div className="meta-info-item">
                <span className="label">User ID:</span>
                <span className="value font-mono">{activeTicket.userId?._id || 'N/A'}</span>
              </div>
            </div>
          </div>

          <div className="meta-card">
            <div className="meta-card-header">
              <BsShieldCheck />
              <h5>Assigned Agent</h5>
            </div>
            <div className="assigned-agent-badge">
              {activeTicket.assignedAgentId?.Name ? activeTicket.assignedAgentId.Name : 'Unassigned Queue'}
            </div>

            {activeTicket.status !== 'closed' && (
              <div className="form-group mt-3">
                <label className="font-weight-bold text-muted" style={{fontSize: '0.75rem'}}>Reassign / Transfer Chat:</label>
                <select className="form-control form-control-sm custom-select-sm" onChange={(e) => handleTransferChat(e.target.value)} defaultValue="">
                  <option value="" disabled>Select agent...</option>
                  {agentsList
                    .filter(a => a._id !== activeTicket.assignedAgentId?._id)
                    .map(a => (
                      <option key={a._id} value={a._id}>
                        {a.Name} ({a.online ? 'Online' : 'Offline'})
                      </option>
                    ))
                  }
                </select>
              </div>
            )}
          </div>

          <div className="meta-card flex-grow-1 d-flex flex-column">
            <div className="meta-card-header">
              <BsFileEarmarkText />
              <h5>Internal Notes</h5>
            </div>
            <textarea 
              className="notes-textarea form-control flex-grow-1"
              placeholder="Write private notes regarding this issue..."
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
            />
            <button className="btn btn-save-notes mt-2 w-100" onClick={handleSaveNotes}>
              Save Notes
            </button>
          </div>
        </div>
      )}

      {/* CALL OVERLAY PANEL */}
      {callActive && (
        <div className="support-call-overlay">
          <div className="call-card">
            <div className="call-avatar">
              <BsFillTelephoneFill size={28} />
            </div>
            {callState === 'calling' && <h4>Calling Customer...</h4>}
            {callState === 'ringing' && <h4>Incoming Voice Call</h4>}
            {callState === 'connected' && (
              <>
                <h4>Call Connected</h4>
                <div className="call-timer-display">{formatDuration(callDuration)}</div>
              </>
            )}

            <div className="call-actions">
              {callState === 'ringing' && (
                <>
                  <button className="call-action-circle accept" onClick={acceptCall}>
                    <BsPlayFill size={24} />
                  </button>
                  <button className="call-action-circle decline" onClick={rejectCall}>
                    <BsXCircle size={22} />
                  </button>
                </>
              )}

              {(callState === 'calling' || callState === 'connected') && (
                <>
                  <button className="call-action-circle mute" onClick={toggleMute} title={isMuted ? 'Unmute' : 'Mute'}>
                    {isMuted ? <BsMicMute size={20} /> : <BsMic size={20} />}
                  </button>
                  <button className="call-action-circle decline" onClick={hangupCall}>
                    <BsXCircle size={22} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminSupportChat;